import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { HumaTekFooter } from "../components/layout/HumaTekFooter";
import {
  ArrowRight,
  BarChart3,
  Globe,
  Shield,
  Zap,
  Users,
  Building2,
  Heart,
} from "lucide-react";

const HERO_IMAGE = process.env.REACT_APP_HERO_IMAGE || "https://images.unsplash.com/photo-1680199489033-bc336166482b";

const features = [
  {
    icon: Globe,
    title: "Real-Time Tracking",
    description: "Track every donation from wallet to impact with live global map visualization.",
  },
  {
    icon: Shield,
    title: "Blockchain Verified",
    description: "Immutable audit trail ensures complete transparency and fraud prevention.",
  },
  {
    icon: BarChart3,
    title: "AI Analytics",
    description: "Smart insights and predictive modeling for optimized aid distribution.",
  },
  {
    icon: Zap,
    title: "Instant Transfers",
    description: "Multi-currency support including crypto with <1% transaction fees.",
  },
];

const stats = [
  { value: "$12M+", label: "Tracked Transparently" },
  { value: "50+", label: "NGO Partners" },
  { value: "25+", label: "Countries" },
  { value: "99.9%", label: "Uptime" },
];

const stakeholders = [
  { icon: Heart, label: "Donors", description: "See exactly where your money goes" },
  { icon: Building2, label: "NGOs", description: "Build trust with transparent reporting" },
  { icon: Users, label: "Governments", description: "Ensure accountability and compliance" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white z-10" />
          <img
            src={HERO_IMAGE}
            alt="Humanitarian aid"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 hero-glow z-20" />
        </div>

        <div className="container mx-auto px-4 pt-32 pb-20 relative z-30">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="outline" className="mb-6 px-4 py-1.5 text-sm border-primary/40 bg-primary/5">
                <span className="text-primary font-medium">Powered by Blockchain & AI</span>
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6 text-slate-800"
              data-testid="hero-title"
            >
              See Exactly Where Every{" "}
              <span className="text-primary">Humanitarian Dollar</span> Goes
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto mb-8"
              data-testid="hero-subtitle"
            >
              The first enterprise platform that tracks donations from wallet to impact
              with real-time transparency and AI-powered compliance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button
                size="lg"
                asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 group"
                data-testid="cta-get-started"
              >
                <Link to="/register">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="text-lg px-8 border-primary/30 text-primary hover:bg-primary/5"
                data-testid="cta-live-dashboard"
              >
                <Link to="/public-dashboard">View Live Dashboard</Link>
              </Button>
            </motion.div>
          </div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-4 rounded-xl bg-white border border-border shadow-sm"
                data-testid={`stat-${index}`}
              >
                <p className="font-heading font-black text-3xl text-primary">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-[#F4F5F7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge variant="destructive" className="mb-4">The Problem</Badge>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4 text-slate-800">
              The $320 Billion Transparency Crisis
            </h2>
            <p className="text-slate-500 text-base md:text-lg">
              Every year, 40% of the $800 billion donated to humanitarian causes
              never reaches its intended recipients due to inefficiency and fraud.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { title: "No Visibility", desc: "Donors never know if their money reached its destination" },
              { title: "High Costs", desc: "Traditional systems lose 40% to overhead and fees" },
              { title: "Fraud Risk", desc: "Limited verification enables misappropriation" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-xl bg-white border border-destructive/20 shadow-sm"
              >
                <h3 className="font-heading font-bold text-xl mb-2 text-slate-800">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">The Solution</Badge>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4 text-slate-800">
              Complete Transparency From Donation to Impact
            </h2>
            <p className="text-slate-500 text-base md:text-lg">
              HumaDash ensures every donation is traceable, verifiable, and delivered
              directly to those in need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 rounded-xl bg-white border border-border hover:border-primary/40 hover:shadow-md transition-all"
                data-testid={`feature-${index}`}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2 text-slate-800">{feature.title}</h3>
                <p className="text-slate-500">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholders Section */}
      <section className="py-24 bg-[#F4F5F7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4 text-slate-800">
              Built for Everyone in the Ecosystem
            </h2>
            <p className="text-slate-500 text-base md:text-lg">
              From individual donors to government agencies, HumaDash serves all stakeholders.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stakeholders.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-xl bg-white border border-border shadow-sm"
              >
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2 text-slate-800">{item.label}</h3>
                <p className="text-slate-500">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-white">
        <div className="absolute inset-0 hero-glow opacity-40" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4 text-slate-800">
              Ready to Transform Humanitarian Aid?
            </h2>
            <p className="text-slate-500 text-base md:text-lg mb-8">
              Join leading NGOs and organizations using HumaDash to ensure every
              dollar reaches those who need it most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8"
                data-testid="cta-bottom-register"
              >
                <Link to="/register">Start Free Trial</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8 border-primary/30 text-primary hover:bg-primary/5">
                <a href={process.env.REACT_APP_HUMADASH_URL || "https://humadash.com"} target="_blank" rel="noopener noreferrer">
                  Learn More
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* HumaTek Footer */}
      <HumaTekFooter />
    </div>
  );
}
